"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo, useRef, useState } from "react";
import MuxVideo from "@mux/mux-video-react";
import Image from "next/image";
import { LazyMotion, domAnimation, m, useReducedMotion } from "framer-motion";
import Breadcrumbs, { type BreadcrumbItem } from "@/components/Breadcrumbs";
import ButtonLandingPage from "@/components/landing/ButtonLandingPage";
import { buildMuxStreamUrl, shouldAvoidMuxAutoplay } from "@/lib/mux";
import type { LocationHeroVm } from "@/lib/locations/types";
const slideUp = {
  hidden: {
    y: 40,
    opacity: 0
  },
  visible: (i: number) => ({
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.8,
      ease: [0.215, 0.61, 0.355, 1],
      delay: i * 0.1
    }
  })
} as const;
const POSTER_SIZES = "(max-width: 1023px) 100vw, 50vw";
const FALLBACK_POSTER = "/hero-poster.jpg";
export type LocationHeroClientProps = LocationHeroVm & {
  breadcrumbs?: BreadcrumbItem[];
};
export function LocationHeroClient(t0) {
  const $ = _c(53);
  const {
    breadcrumbs,
    heading,
    description,
    poster,
    playbackId
  } = t0;
  const prefersReduced = useReducedMotion();
  const variants = prefersReduced ? undefined : slideUp;
  const videoRef = useRef(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const [posterSrc, setPosterSrc] = useState(poster.src);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = shouldAvoidMuxAutoplay();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const avoidAutoplay = t1;
  const useVideo = Boolean(playbackId) && !videoFailed;
  const canAutoplay = useVideo && !avoidAutoplay;
  let t2;
  if ($[1] !== playbackId || $[2] !== videoFailed) {
    t2 = playbackId && !videoFailed ? buildMuxStreamUrl(playbackId) : null;
    $[1] = playbackId;
    $[2] = videoFailed;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const streamUrl = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const el = videoRef.current;
      if (!el?.play) {
        return;
      }
      el.play().catch(_temp);
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const primeAndPlay = t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = event => {
      if (event.currentTarget.currentTime > 0.1) {
        setIsVideoPlaying(true);
      }
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const handleTimeUpdate = t4;
  const hasBreadcrumbs = Boolean(breadcrumbs && breadcrumbs.length > 0);
  const t5 = hasBreadcrumbs ? 1 : 0;
  let t6;
  if ($[6] !== canAutoplay || $[7] !== streamUrl) {
    t6 = canAutoplay && streamUrl ? <div className="absolute inset-0 z-10"><MuxVideo ref={videoRef} src={streamUrl} autoPlay={true} muted={true} loop={true} playsInline={true} preload="auto" onCanPlay={primeAndPlay} onTimeUpdate={handleTimeUpdate} onError={() => setVideoFailed(true)} className="h-full w-full" style={{
        height: "100%",
        width: "100%",
        objectFit: "cover",
        objectPosition: "center"
      }} /></div> : null;
    $[6] = canAutoplay;
    $[7] = streamUrl;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = canAutoplay && isVideoPlaying ? 0 : 1;
  let t8;
  if ($[9] !== t7) {
    t8 = {
      opacity: t7,
      transition: "opacity 0.8s ease-in-out"
    };
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const t9 = poster.alt;
  let t10;
  if ($[11] !== posterSrc) {
    t10 = posterSrc.startsWith("https://image.mux.com/");
    $[11] = posterSrc;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== posterSrc) {
    t11 = () => {
      if (posterSrc !== FALLBACK_POSTER) {
        setPosterSrc(FALLBACK_POSTER);
      }
    };
    $[13] = posterSrc;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== poster.alt || $[16] !== posterSrc || $[17] !== t10 || $[18] !== t11) {
    t12 = <Image src={posterSrc} alt={t9} fill={true} priority={true} sizes={POSTER_SIZES} className="object-cover" unoptimized={t10} onError={t11} />;
    $[15] = poster.alt;
    $[16] = posterSrc;
    $[17] = t10;
    $[18] = t11;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== t12 || $[21] !== t8) {
    t13 = <div className="absolute inset-0 z-20 pointer-events-none" style={t8}>{t12}</div>;
    $[20] = t12;
    $[21] = t8;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <span className="absolute inset-0 z-30 bg-black/25 pointer-events-none" aria-hidden="true" />;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t13 || $[25] !== t5 || $[26] !== t6 || $[27] !== variants) {
    t15 = <m.div variants={variants} custom={t5} className="relative order-2 aspect-[1/1] w-full overflow-hidden rounded-3xl bg-black md:order-1 md:aspect-[5/4]">{t6}{t13}{t14}</m.div>;
    $[24] = t13;
    $[25] = t5;
    $[26] = t6;
    $[27] = variants;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== breadcrumbs || $[30] !== hasBreadcrumbs || $[31] !== variants) {
    t16 = hasBreadcrumbs ? <m.div variants={variants} custom={0} className="mb-4 md:mb-6"><Breadcrumbs items={breadcrumbs} /></m.div> : null;
    $[29] = breadcrumbs;
    $[30] = hasBreadcrumbs;
    $[31] = variants;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  const t17 = hasBreadcrumbs ? 1 : 0;
  let t18;
  if ($[33] !== heading || $[34] !== t17 || $[35] !== variants) {
    t18 = <m.h1 variants={variants} custom={t17} className="mb-5 text-4xl font-medium md:mb-6 md:text-8xl">{heading}</m.h1>;
    $[33] = heading;
    $[34] = t17;
    $[35] = variants;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  const t19 = hasBreadcrumbs ? 2 : 1;
  let t20;
  if ($[37] !== description || $[38] !== t19 || $[39] !== variants) {
    t20 = <m.p variants={variants} custom={t19} className="md:text-md">{description}</m.p>;
    $[37] = description;
    $[38] = t19;
    $[39] = variants;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  const t21 = hasBreadcrumbs ? 3 : 2;
  let t22;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <ButtonLandingPage label="Napisz do nas" />;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== t21 || $[43] !== variants) {
    t23 = <m.div variants={variants} custom={t21} className="mt-6 flex flex-wrap gap-4 md:mt-8">{t22}</m.div>;
    $[42] = t21;
    $[43] = variants;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  let t24;
  if ($[45] !== t16 || $[46] !== t18 || $[47] !== t20 || $[48] !== t23) {
    t24 = <div className="order-1 md:order-2">{t16}{t18}{t20}{t23}</div>;
    $[45] = t16;
    $[46] = t18;
    $[47] = t20;
    $[48] = t23;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== t15 || $[51] !== t24) {
    t25 = <LazyMotion features={domAnimation}><m.section id="relume" initial="hidden" animate="visible" className="overflow-hidden px-[5%] pt-28 pb-16 md:pt-36 md:pb-24 lg:pt-44 lg:pb-28"><div className="container"><div className="grid grid-cols-1 gap-x-20 gap-y-12 md:gap-y-16 lg:grid-cols-2 lg:items-center">{t15}{t24}</div></div></m.section></LazyMotion>;
    $[50] = t15;
    $[51] = t24;
    $[52] = t25;
  } else {
    t25 = $[52];
  }
  return t25;
}
function _temp() {}